import { MdClose, MdDeleteOutline } from 'react-icons/md';
import { useAdvisor } from '../context/AdvisorContext';

// Read-only memory transparency panel. Shown when ctx.memoryViewOpen is true.
// Doubles as user-facing trust feature (see what the AI remembers about you)
// and demo aid (paste a screenshot when explaining the memory feature).
export default function MemoryViewModal() {
  const ctx = useAdvisor();
  if (!ctx.memoryViewOpen) return null;
  const m = ctx.memory;

  const handleForget = () => {
    if (window.confirm('Forget everything? This clears all stored memory — the next session will be a fresh cold start.')) {
      ctx.resetMemory();
      ctx.closeMemoryView();
    }
  };

  return (
    <div className="memory-modal-backdrop" onClick={ctx.closeMemoryView}>
      <div className="memory-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="memory-modal__header">
          <div>
            <div className="memory-modal__title">What I Remember</div>
            <div className="memory-modal__subtitle">Everything the AI is using to keep your conversations continuous.</div>
          </div>
          <button className="memory-modal__close" onClick={ctx.closeMemoryView} aria-label="Close" type="button">
            <MdClose size={18} />
          </button>
        </div>

        {!m ? (
          <div className="memory-modal__empty">No memory stored yet. Have a conversation or click Seed to populate.</div>
        ) : (
          <div className="memory-modal__body">
            <Field label="First session"  value={m.firstSession?.slice(0, 10) || '—'} />
            <Field label="Most recent"    value={m.lastSession?.slice(0, 10) || '—'} />
            <Field label="Sessions total" value={m.sessionCount || 1} />
            <Field label="Working with"   value={m.selectedAdvisor || '—'} />

            {m.profile && (
              <Field
                label="Strategy"
                value={
                  <>
                    {m.profile}
                    {m.tilts ? (
                      <span className="memory-modal__muted">
                        {' '}· tilts: short {m.tilts.shortPercent || 0}pp · gold {m.tilts.goldPercent || 0}pp · bonds {m.tilts.bondsPercent || 0}pp
                      </span>
                    ) : null}
                  </>
                }
              />
            )}
            {m.allocationUsd != null && (
              <Field label="Allocation" value={`$${Number(m.allocationUsd).toLocaleString()}`} />
            )}

            <ChipList label="Goals"       items={m.goals} />
            <ChipList label="Concerns"    items={m.concerns} />
            <ChipList label="Preferences" items={m.preferences} />

            {m.recentSessions?.length > 0 && (
              <div className="memory-modal__section">
                <div className="memory-modal__field-label">Recent sessions</div>
                <ul className="memory-modal__sessions">
                  {m.recentSessions.map((s, i) => (
                    <li key={i}>
                      <div className="memory-modal__session-date">{s.date}</div>
                      <div className="memory-modal__session-summary">{s.summary || '(no summary)'}</div>
                      {s.followUps?.length > 0 && (
                        <div className="memory-modal__session-followups">
                          Follow-ups: {s.followUps.join(' · ')}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="memory-modal__footer">
          <button className="memory-modal__forget" onClick={handleForget} type="button">
            <MdDeleteOutline size={16} /> Forget everything
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="memory-modal__field">
      <span className="memory-modal__field-label">{label}</span>
      <span className="memory-modal__field-value">{value}</span>
    </div>
  );
}

function ChipList({ label, items }) {
  if (!items?.length) return null;
  return (
    <div className="memory-modal__section">
      <div className="memory-modal__field-label">{label}</div>
      <div className="memory-modal__chips">
        {items.map((it, i) => <span key={i} className="memory-modal__chip">{it}</span>)}
      </div>
    </div>
  );
}
